import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import {IconButton, InputAdornment, Popover, Stack, Tooltip} from "@mui/material";
import React, {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import EmojiPicker from "../../../common/emojiPicker/EmojiPicker";
import {
    InlineInput,
    InlineLabelledField,
    localizedValue,
    patchLocalized,
    QuestionEditorProps
} from "./questionEditor.types";

type ScaleLabelField = 'lowLabel' | 'highLabel';

function ScaleLabelFields(props: QuestionEditorProps) {
    const {t} = useTranslation();
    const {question, language, disabled} = props;
    const lowInput = useRef<HTMLInputElement>(null);
    const highInput = useRef<HTMLInputElement>(null);
    const [emojiTarget, setEmojiTarget] = useState<{
        anchor: HTMLElement
        field: ScaleLabelField
    } | null>(null);

    const inputFor = (field: ScaleLabelField) => field === 'lowLabel' ? lowInput.current : highInput.current;

    const emojiAdornment = (field: ScaleLabelField) => (
        <InputAdornment position={'end'}>
            <Tooltip title={t('Insert emoji')}>
                <span>
                    <IconButton
                        size={'small'}
                        aria-label={t('Insert emoji')}
                        disabled={disabled}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => setEmojiTarget({anchor: event.currentTarget, field})}
                        sx={{mr: -0.5}}
                    >
                        <EmojiEmotionsOutlinedIcon fontSize={'small'}/>
                    </IconButton>
                </span>
            </Tooltip>
        </InputAdornment>
    );

    const insertEmoji = (field: ScaleLabelField, emoji: string) => {
        const input = inputFor(field);
        const value = localizedValue(question[field], language);
        const start = input?.selectionStart ?? value.length;
        const end = input?.selectionEnd ?? start;
        const nextCaret = start + emoji.length;

        patchLocalized(props, field, `${value.slice(0, start)}${emoji}${value.slice(end)}`);
        setEmojiTarget(null);

        requestAnimationFrame(() => {
            input?.focus();
            input?.setSelectionRange(nextCaret, nextCaret);
        });
    };

    return (
        <>
            <Stack direction={{xs: 'column', sm: 'row'}} gap={1.5}>
                <InlineLabelledField label={t('Low label')}>
                    <InlineInput
                        value={localizedValue(question.lowLabel, language)}
                        onChange={(value) => patchLocalized(props, 'lowLabel', value)}
                        placeholder={t('Optional')}
                        disabled={disabled}
                        inputRef={lowInput}
                        endAdornment={emojiAdornment('lowLabel')}
                    />
                </InlineLabelledField>

                <InlineLabelledField label={t('High label')}>
                    <InlineInput
                        value={localizedValue(question.highLabel, language)}
                        onChange={(value) => patchLocalized(props, 'highLabel', value)}
                        placeholder={t('Optional')}
                        disabled={disabled}
                        inputRef={highInput}
                        endAdornment={emojiAdornment('highLabel')}
                    />
                </InlineLabelledField>
            </Stack>

            <Popover
                open={!!emojiTarget}
                anchorEl={emojiTarget?.anchor}
                onClose={() => setEmojiTarget(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                transitionDuration={0}
                slotProps={{paper: {sx: {borderRadius: 2, backgroundColor: 'transparent'}}}}
                sx={theme => ({zIndex: theme.zIndex.modal - 1})}
            >
                <EmojiPicker
                    onSelect={(selection) => {
                        if (emojiTarget) insertEmoji(emojiTarget.field, selection.emoji);
                    }}
                />
            </Popover>
        </>
    );
}

export default ScaleLabelFields;
